import api from '@/api'
import Vue from 'vue';
import permissions from '@/api/permissions'
import _ from 'lodash'

export default {
  state: {
    tenders: {}
  },

  getters: {
    tender: s => tenderId => s.tenders[tenderId]
  },

  mutations: {
    setTender(s, data) {
      const model = {
        id: data.id,
        hasPublic: !!data.hasPublic,
        modifiedTime: data.lastChange,
        creationTime: data.time,
        publication: {
          mode: new api.TenderPublicationMode(!!data.timePublished.now ? api.TenderPublicationMode.NOW : api.TenderPublicationMode.DEFERRED),
          time: new Date(data.timePublished.time)
        },
        status: new api.TenderState(data.state),
        reason: data.reason,
        changeDescription: data.changeDescription,
        hasSign: !!data.hasSign,
        owner: {
          id: data.owner,
          name: [data.lastName, data.firstName, data.middleName].join(' '),
          firstName: data.firstName,
          lastName: data.lastName,
          middleName: data.middleName,
          email: data.email,
          phone: data.phone
        },
        isFilled: data.filled,
        name: data.name || undefined,
        number: data.num || undefined,
        method: new api.TenderMethod(data.method),

        durations: {
          receivingRequests: {
            value: api.Number.fromAPI(data.gettingRequestDays),
          },
          reviewingRequests: {
            value: api.Number.fromAPI(data.processingRequestDays),
            isPublic: !!data.viewProcessingRequestDays
          },
          reviewingRequestsByCriteria: {
            value: api.Number.fromAPI(data.processingRequestByCriterionDays),
            isPublic: !!data.viewProcessingRequestByCriterionDays
          },
          waitingForFirstRetend: {
            value: api.Number.fromAPI(data.waitingFirstRetendDays),
            isPublic: !!data.viewWaitingFirstRetendDays
          },
          splittingPrice: {
            value: api.Number.fromAPI(data.dividingPriceDays),
            isPublic: !!data.viewDividingPriceDays
          },
          tender: {
            value: api.Number.fromAPI(data.tenderDays),
            isPublic: !!data.viewTenderDays
          },
          contractConclustion: {
            value: api.Number.fromAPI(data.contractDays),
            isPublic: !!data.viewContractDays
          },
          remind: api.Number.fromAPI(data.remindDays),

          waitingForFirstBid: {
            value: data.timeWaitingFirst
          },

          procedureExtensionTime: {
            value: data.timeExtension
          }
        },

        isOrganizerSignatureRequired: data.signOrganizer,
        isParticipantSignatureRequired: data.signParticipant,
        doShowWeights: data.showWeight,

        lots: data.lots || [],
        files: data.files,
        commission: {
          chairman: _.find(data.commission, { chairman: true }) || {},
          deputyChairman: _.find(data.commission, { deputyChairman: true }) || {},
          secretary: _.find(data.commission, { secretary: true }) || {},
          other: data.commission.filter(e => !e.chairman && !e.deputyChairman && !e.secretary)
        },
        criteria: {
          price: _.orderBy(data.criterions.filter(e => e.type === 'Price' || e.type === 'PriceLot').map(e => ({
            values: undefined,
            ...e,
            minValue: api.Number.fromAPI(e.minValue, '', 0),
            maxValue: api.Number.fromAPI(e.maxValue, '', ''),
            editable: undefined,
            readonly: !e.editable,
            unit: new api.AssessmentUnit(e.positionType),
            format: new api.ProposalFormat(e.view),
            id: e.criterion
          })), 'id'),
          expert: _.orderBy(data.criterions.filter(e => e.type === 'Expert').map(e => ({
            ...e,
            id: e.criterion,
            unit: new api.AssessmentUnit(e.positionType),
          })), 'id'),
          reduction: _.orderBy(data.criterions.filter(e => e.type === 'Reduction').map(e => ({
            ...e,
            minValue: api.Number.fromAPI(e.minValue, '', 0),
            maxValue: api.Number.fromAPI(e.maxValue, '', ''),
            id: e.criterion,
            unit: new api.AssessmentUnit(e.positionType),
            editable: undefined,
            readonly: !e.editable,
            format: new api.ProposalFormat(e.view),
          })), 'id'),
          reducedPrice: _.orderBy(data.criterions.filter(e => e.type === 'ReducedPrice').map(e => ({
            ...e,
            id: e.criterion
          })), 'id')
        },
        documents: data.requestDocuments.map(e => ({
          type: new api.TenderDocumentType(e.type),
          document: e
        })),
        permissions: permissions.init(data.permissions),
        needVerify: !!data.needVerify,
        formula: data.formula
      };

      Vue.set(s.tenders, model.id, model);
    }
  },

  actions: {
    handleTender({commit}, data) {
      commit('setTender', data);
    },

    async createTender({}) {
      try {
        const response = await api.post(`tenders`);
        const {status, result} = response.data;

        if (status === 200) {
          return result;
        }
      }
      catch(e) {

      }
    },

    async fetchTender({dispatch, getters}, {tenderId}) {
      try {
        const response = await api.get(`/tenders/${tenderId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(tenderId);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async updateTender({dispatch, getters}, tender) {
      try {

        const response = await api.put(`/tenders/${tender.id}`, {
          owner: tender.owner.id,
          name: tender.name,
          num: tender.number,
          changeDescription: tender.changeDescription,
          timePublishedNow: tender.publication.mode == api.TenderPublicationMode.NOW,
          timePublished: tender.publication.mode == api.TenderPublicationMode.DEFERRED ? (+tender.publication.time || Date.now()) : undefined,
          gettingRequestDays: tender.durations.receivingRequests.value,
          processingRequestDays: tender.durations.reviewingRequests.value,
          viewProcessingRequestDays: tender.durations.reviewingRequests.isPublic,
          processingRequestByCriterionDays: tender.durations.reviewingRequestsByCriteria.value,
          viewProcessingRequestByCriterionDays: tender.durations.reviewingRequestsByCriteria.isPublic,
          waitingFirstRetendDays: tender.durations.waitingForFirstRetend.value,
          viewWaitingFirstRetendDays: tender.durations.waitingForFirstRetend.isPublic,
          dividingPriceDays: tender.durations.splittingPrice.value,
          viewDividingPriceDays: tender.durations.splittingPrice.isPublic,
          tenderDays: tender.durations.tender.value,
          viewTenderDays: tender.durations.tender.isPublic,
          contractDays: tender.durations.contractConclustion.value,
          viewContractDays: tender.durations.contractConclustion.isPublic,
          timeWaitingFirst: tender.durations.waitingForFirstBid.value,
          timeExtension: tender.durations.procedureExtensionTime.value,
          formula: tender.formula,
          remindDays: (_.some(tender.documents, e => e.document.passport) ? +tender.durations.remind : undefined) || undefined,
          method: tender.method.valueOf(),
          signOrganizer: tender.isOrganizerSignatureRequired,
          signParticipant: tender.isParticipantSignatureRequired,
          showWeight: tender.doShowWeights,
          commission: {
            chairman: +_.get(tender.commission.chairman, 'id') || undefined,
            deputyChairman: +_.get(tender.commission.deputyChairman, 'id') || undefined,
            secretary: +_.get(tender.commission.secretary, 'id') || undefined,
            other: tender.commission.other.map(e => +e.id)
          },
          requestDocuments: tender.documents.map(e => ({document: e.document.id, type: e.type.valueOf()})),
          lots: tender.lots.map(e => e.id),
          // files: tender.files.map(e => e.id),
          criterions: tender.criteria.price.map(e => ({
            criterion: e.id,
            type: e.type,
            weight: e.weight,
            minValue: e.format.NUMBER ? e.minValue : undefined,
            maxValue: e.format.NUMBER ? e.maxValue : undefined,
            positionType: e.unit.valueOf(),
            view: e.format.valueOf(),
            values: e.values && e.values.length ? e.values : undefined,
            okei: e.okei
          })).concat(tender.criteria.expert.map(e => ({
            criterion: e.id,
            type: e.type,
            weight: e.weight,
            positionType: e.unit && e.unit.valueOf(),
          }))).concat(tender.criteria.reduction.map(e => ({
            criterion: e.id,
            type: e.type,
            weight: e.weight,
            positionType: e.unit && e.unit.valueOf(),
            minValue: e.format.NUMBER ? e.minValue : undefined,
            maxValue: e.format.NUMBER ? e.maxValue : undefined,
            view: e.format.valueOf(),
            values: e.values && e.values.length ? e.values : undefined,
            okei: e.okei
          }))).concat(tender.criteria.reducedPrice.map(e => ({
            criterion: e.id,
            type: e.type,
            weight: e.weight
          })))
        });
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async fetchPossibleCommissionMembers({}, { id }) {
      try {
        const response = await api.get(`tenders/${id}/commission`);
        const {status, result} = response.data;

        if (status === 200) {
          return result.map(e => ({
            id: e.id,
            firstName: e.firstName,
            lastName: e.lastName,
            middleName: e.middleName
          }))
        }
      }
      catch(e) {

      }
    },

    async fetchPossibleLots({}) {
      try {
        const response = await api.get(`tenders/lots/assign`);
        const {status, result} = response.data;

        if (status === 200) {
          return result;
        }
      }
      catch(e) {

      }
    },

    async addFileToTender({dispatch, getters}, {tenderId, sign, file}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        const response = await api.post(`tenders/${tenderId}/attach`, data);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async removeFileFromTender({dispatch, getters}, {tenderId, fileId}) {
      try {
        const response = await api.delete(`tenders/${tenderId}/${fileId}/attach`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async getTenderSigningText({}, {id} ) {
      try {
        const response = await api.get(`tenders/${id}/sign`);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          return result;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async publishTender({dispatch, getters}, { id, text, signature }) {
      try {
        const response = await api.put(`tenders/${id}/commit`, {
          dataForSign: text,
          sign: signature
        });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
        throw e;
      }
    },

    async acceptTender({dispatch, getters}, { id }) {
      try {
        const response = await api.post(`/tenders/${id}/approved`, { accept: true });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async deleteTender({dispatch, getters}, { id }) {
      try {
        const response = await api.delete(`/tenders/${id}`, { accept: true });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          return;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async declineTender({dispatch, getters}, { id, reason }) {
      try {
        const response = await api.post(`/tenders/${id}/approved`, { accept: false, reason });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleTender', result);
          return getters.tender(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async checkTenderFileSignature({}, {tenderId, fileId}) {
      try {
        const response = await api.get(`/tenders/${tenderId}/info/sign/${fileId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async checkTenderSignature({}, { tenderId }) {
      try {
        const response = await api.get(`/tenders/${tenderId}/info/sign/0`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },
  }
}
